"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssignmentClient = void 0;
const dp_http_client_lib_1 = require("@greatmindsorg/dp-http-client-lib");
const sdk_constants_1 = require("../../constants/sdk.constants");
const assignment_serializer_1 = require("../serializers/assignment.serializer");
const assignment_progress_serializer_1 = require("../serializers/assignment-progress.serializer");
const bundled_assignment_serializer_1 = require("../serializers/bundled-assignment.serializer");
const helpers_1 = require("../../helpers");
const student_helper_1 = require("../helpers/student.helper");
const get_1 = __importDefault(require("lodash/get"));
class AssignmentClient {
    constructor(client, serializer, assignmentProgressSerializer, bundledAssignmentSerializer) {
        this.client = client;
        this.serializer = serializer;
        this.assignmentProgressSerializer = assignmentProgressSerializer;
        this.bundledAssignmentSerializer = bundledAssignmentSerializer;
        this.studentHelper = new student_helper_1.StudentHelper(AssignmentClient.acmaSDKOptions, AssignmentClient.acmaSDKRetryOptions);
    }
    static newInstance(options, retryOptions, callback) {
        AssignmentClient.pastSchoolYear = callback;
        AssignmentClient.acmaSDKOptions = {
            ...options,
            baseURL: (0, helpers_1.removeAssignmentsPath)((0, get_1.default)(options, 'baseURL', undefined)),
        };
        AssignmentClient.acmaSDKRetryOptions = retryOptions;
        return new AssignmentClient(dp_http_client_lib_1.RetryableHttpClient.newInstance(options, retryOptions), new assignment_serializer_1.AssignmentSerializer(), new assignment_progress_serializer_1.AssignmentProgressSerializer(), new bundled_assignment_serializer_1.BundledAssignmentSerializer());
    }
    async findUserIdForAssigneeIdsList(assignments) {
        const assignmentList = [];
        for (let assignment of assignments) {
            const assigneeIds = await this.studentHelper.transformToStudentIds(assignment.assigneeIds);
            assignmentList.push({
                ...assignment,
                assigneeIds,
            });
        }
        return assignmentList;
    }
    async assign(assignments, headers, launch = false) {
        const assignmentList = await this.findUserIdForAssigneeIdsList(assignments);
        const response = await this.client.post(`${sdk_constants_1.API_PREFIX}/assignments.assign`, {
            assignments: assignmentList,
        }, {
            headers,
            params: { launch },
        });
        return this.serializer.serialize(response.body);
    }
    async launch(id, headers) {
        const response = await this.client.put(`${sdk_constants_1.API_PREFIX}/assignments.launch/${id}`, {}, {
            headers,
        });
        const { data } = response.body;
        return this.serializer.serializeOne(data);
    }
    async reopen(id, headers, assignment) {
        const assigneeIds = await this.studentHelper.transformToStudentIds(assignment.assigneeIds || []);
        const response = await this.client.put(`${sdk_constants_1.API_PREFIX}/assignments.reopen/${id}`, {
            ...assignment,
            assigneeIds,
        }, {
            headers,
        });
        const { data } = response.body;
        return this.serializer.serializeOne(data);
    }
    async findByOwner(filters, pageableOptions, headers) {
        const response = await this.client.get(`${sdk_constants_1.API_PREFIX}/assignments.findByOwner`, {
            headers: (0, helpers_1.setPastSchoolYear)(headers, AssignmentClient.pastSchoolYear),
            params: { ...filters, ...pageableOptions },
        });
        response.body.data = response.body.data.map((resourcesInAssignment) => {
            const assignment = resourcesInAssignment.assignment;
            assignment.resourceIds = resourcesInAssignment.resourceIds;
            assignment.resourceParentIds = resourcesInAssignment.resourceParentIds;
            if (resourcesInAssignment.assignmentProgress) {
                const { totalInProgress, totalOpen, totalAssigned, totalSubmitted } = resourcesInAssignment.assignmentProgress;
                assignment.totalNotStarted = totalOpen + totalAssigned;
                assignment.totalSubmitted = totalSubmitted;
                assignment.totalInProgress = totalInProgress;
            }
            return assignment;
        });
        return this.serializer.serialize(response.body);
    }
    async findStatusTotals(assignmentIds, contextId, curriculumId, headers) {
        const response = await this.client.get(`${sdk_constants_1.API_PREFIX}/assignments.findStatusTotals`, {
            headers: (0, helpers_1.setPastSchoolYear)(headers, AssignmentClient.pastSchoolYear),
            params: { assignmentIds, contextId, curriculumId },
        });
        const assignmentsData = {
            data: response.body.data.assignments,
        };
        return {
            assignments: this.serializer.serialize(assignmentsData),
            totals: this.serializer.serializeStatusTotals(response.body.data.totals),
        };
    }
    async findOne(id, headers) {
        const response = await this.client.get(`${sdk_constants_1.API_PREFIX}/assignments/${id}`, {
            headers: (0, helpers_1.setPastSchoolYear)(headers, AssignmentClient.pastSchoolYear),
        });
        const { data } = response.body;
        return this.serializer.serializeOne(data);
    }
    async stop(id, headers) {
        const response = await this.client.put(`${sdk_constants_1.API_PREFIX}/assignments.stop/${id}`, {}, {
            headers,
        });
        const { data } = response.body;
        return this.serializer.serializeOne(data);
    }
    async finish(id, headers) {
        const response = await this.client.put(`${sdk_constants_1.API_PREFIX}/assignments.finish/${id}`, {}, {
            headers,
        });
        const { data } = response.body;
        return this.serializer.serializeOne(data);
    }
    async findAssignmentsProgress(assignmentIds, headers) {
        const response = await this.client.get(`${sdk_constants_1.API_PREFIX}/assignments.findAssignmentsProgress`, {
            headers: (0, helpers_1.setPastSchoolYear)(headers, AssignmentClient.pastSchoolYear),
            params: { assignmentIds },
        });
        return this.assignmentProgressSerializer.serialize(response.body);
    }
    async findByResource(resourceId, pageableOptions, headers) {
        const response = await this.client.get(`${sdk_constants_1.API_PREFIX}/assignments.findByResource/${resourceId}`, {
            headers: (0, helpers_1.setPastSchoolYear)(headers, AssignmentClient.pastSchoolYear),
            params: { ...pageableOptions },
        });
        return this.serializer.serialize(response.body);
    }
    async findByBundleId(bundleId, headers) {
        const response = await this.client.get(`${sdk_constants_1.API_PREFIX}/assignments.findByBundleId/${bundleId}`, {
            headers: (0, helpers_1.setPastSchoolYear)(headers, AssignmentClient.pastSchoolYear),
        });
        return this.bundledAssignmentSerializer.serialize(response.body);
    }
    async deleteAssignment(id, headers) {
        return await this.client.put(`${sdk_constants_1.API_PREFIX}/assignments.delete/${id}`, {}, {
            headers,
        });
    }
    async edit(id, assignment, headers) {
        const response = await this.client.put(`${sdk_constants_1.API_PREFIX}/assignments.edit/${id}`, {
            ...assignment,
        }, {
            headers,
        });
        const { data } = response.body;
        return this.serializer.serializeOne(data);
    }
    async bundleDetailAssignments(bundleId, headers) {
        const response = await this.client.get(`${sdk_constants_1.API_PREFIX}/assignments.bundleDetail/${bundleId}`, {
            headers,
        });
        const { data } = response.body;
        return this.bundledAssignmentSerializer.serializeDetail(data);
    }
    async getStudentStatusList(assignmentId, studentIds, headers) {
        const response = await this.client.get(`${sdk_constants_1.API_PREFIX}/assignments.students`, {
            headers: (0, helpers_1.setPastSchoolYear)(headers, AssignmentClient.pastSchoolYear),
            params: { assignmentId, studentIds },
        });
        const { data } = response.body;
        return this.serializer.serializeStudents(data);
    }
}
exports.AssignmentClient = AssignmentClient;
//# sourceMappingURL=assignment.client.js.map